// Phase 2 — Opérations: Fournisseurs, Clients, Achats, Stock, Facturation
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, CheckCircle2, XCircle, Truck, Banknote, FileText, Scan } from "lucide-react";
import GenericModule from "@/components/GenericModule";
import Modal from "@/components/Modal";
import { api, formatXAF, formatDate } from "@/lib/api";

// ==================== FOURNISSEURS ====================
export function Suppliers() {
  return (
    <GenericModule
      title="Fournisseurs"
      subtitle="Annuaire des fournisseurs"
      dataTestId="suppliers"
      endpoint="/suppliers"
      newButtonLabel="Nouveau fournisseur"
      extractStats={(d) => [{ label: "Total", value: (d || []).length, count: true, accent: "citron", highlight: true }]}
      columns={[
        { key: "name", label: "Nom" },
        { key: "contact", label: "Contact" },
        { key: "phone", label: "Téléphone", type: "code" },
        { key: "email", label: "Email", type: "code" },
        { key: "nif", label: "NIF", type: "code" },
        { key: "rccm", label: "RCCM", type: "code" },
      ]}
      fields={[
        { key: "name", label: "Nom", required: true, group: 1 },
        { key: "contact", label: "Contact", group: 1 },
        { key: "phone", label: "Téléphone", group: 2 },
        { key: "email", label: "Email", type: "email", group: 2 },
        { key: "address", label: "Adresse", group: 3 },
        { key: "nif", label: "NIF (Fiscal)", group: 3 },
        { key: "rccm", label: "RCCM", group: 3 },
        { key: "bank_account", label: "Compte bancaire" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ name: "", contact: "", phone: "", email: "", address: "", nif: "", rccm: "", bank_account: "", notes: "" }}
    />
  );
}

// ==================== CLIENTS ====================
export function Clients() {
  return (
    <GenericModule
      title="Clients"
      subtitle="Annuaire des clients"
      dataTestId="clients"
      endpoint="/clients"
      newButtonLabel="Nouveau client"
      extractStats={(d) => [{ label: "Total clients", value: (d || []).length, count: true, accent: "citron" }]}
      columns={[
        { key: "name", label: "Nom" },
        { key: "contact", label: "Contact" },
        { key: "phone", label: "Téléphone", type: "code" },
        { key: "email", label: "Email", type: "code" },
        { key: "credit_limit", label: "Plafond crédit", type: "money", align: "right" },
        { key: "nif", label: "NIF", type: "code" },
      ]}
      fields={[
        { key: "name", label: "Nom", required: true, group: 1 },
        { key: "contact", label: "Contact", group: 1 },
        { key: "phone", label: "Téléphone", group: 2 },
        { key: "email", label: "Email", type: "email", group: 2 },
        { key: "address", label: "Adresse" },
        { key: "nif", label: "NIF (Fiscal)", group: 3 },
        { key: "rccm", label: "RCCM", group: 3 },
        { key: "credit_limit", label: "Plafond crédit (XAF)", type: "number", group: 3 },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ name: "", contact: "", phone: "", email: "", address: "", nif: "", rccm: "", credit_limit: 0, notes: "" }}
    />
  );
}

// ==================== ACHATS ====================
export function Purchases() {
  const [viewing, setViewing] = useState(null);
  async function decide(id, status) {
    await api.patch(`/purchases/${id}/status`, { status });
    setViewing(null);
    window.location.reload(); // simple reload to refresh after action
  }

  return (
    <>
      <GenericModule
        title="Achats — Bons de commande"
        subtitle="Demande, validation, réception, paiement"
        dataTestId="purchases"
        endpoint="/purchases"
        newButtonLabel="Nouveau BC"
        headerExtra={
          <Link
            to="/app/ocr"
            className="btn-secondary border-citron/40 text-citron hover:bg-citron/10"
            data-testid="purchases-ocr-link"
            title="Scanner une facture (OCR)"
          >
            <Scan size={14} /> OCR Facture
          </Link>
        }
        extractStats={(d) => [
          { label: "Brouillons", value: d.total_draft || 0, money: true },
          { label: "En attente", value: d.total_pending || 0, money: true, accent: "warn" },
          { label: "Approuvés", value: d.total_approved || 0, money: true, accent: "ok", highlight: true },
        ]}
        columns={[
          { key: "po_number", label: "N° BC", type: "code" },
          { key: "date", label: "Date", type: "date" },
          { key: "supplier_name", label: "Fournisseur" },
          { key: "lines", label: "Lignes", render: (r) => <span className="text-xs text-obsidian-400">{r.lines?.length || 0}</span> },
          { key: "total", label: "Total TTC", type: "money", align: "right" },
          {
            key: "status",
            label: "Statut",
            type: "pill",
            pillClass: (r) => ({
              draft: "bg-obsidian-800 text-obsidian-300",
              pending: "bg-warn/15 text-warn",
              approved: "bg-ok/15 text-ok",
              rejected: "bg-bad/15 text-bad",
              received: "bg-citron/15 text-citron",
              paid: "bg-citron text-obsidian-950",
            })[r.status],
          },
        ]}
        rowActions={(row) => (
          <button onClick={() => setViewing(row)} className="btn-ghost h-7 w-7 p-0 text-citron" title="Voir">
            <Eye size={12} />
          </button>
        )}
        fields={[
          { key: "supplier_name", label: "Fournisseur", required: true, group: 1 },
          { key: "date", label: "Date", type: "date", required: true, group: 2 },
          { key: "expected_date", label: "Livraison prévue", type: "date", group: 2 },
          { key: "lines_text", label: "Lignes (description | qté | PU) — une par ligne", type: "textarea", placeholder: "Ciment 50 sacs | 50 | 8000\nFer à béton | 200 | 5000", required: true },
          { key: "notes", label: "Notes", type: "textarea" },
        ]}
        defaults={{ supplier_name: "", date: new Date().toISOString().slice(0, 10), expected_date: "", lines_text: "", notes: "" }}
        customForm={({ form, setForm, submit, close }) => (
          <PurchaseForm form={form} setForm={setForm} submit={submit} close={close} />
        )}
      />
      <PurchaseDetailModal item={viewing} onClose={() => setViewing(null)} onDecide={decide} />
    </>
  );
}

function PurchaseForm({ form, setForm, submit, close }) {
  // Parse lines from text
  async function onSubmit(e) {
    e.preventDefault();
    const lines = (form.lines_text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const parts = l.split("|").map((p) => p.trim());
        return { description: parts[0] || "", qty: parseFloat(parts[1] || 0), unit_price: parseFloat(parts[2] || 0) };
      })
      .filter((l) => l.description && l.qty > 0);
    if (lines.length === 0) return alert("Ajoutez au moins une ligne");
    try {
      await api.post("/purchases", {
        entity_id: form.entity_id,
        supplier_name: form.supplier_name,
        date: form.date,
        expected_date: form.expected_date || null,
        lines,
        notes: form.notes || "",
      });
      close();
      window.location.reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label block">Fournisseur *</label>
          <input className="input" required value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
        </div>
        <div>
          <label className="label block">Date *</label>
          <input type="date" className="input" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="label block">Livraison prévue</label>
          <input type="date" className="input" value={form.expected_date || ""} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="label block">Lignes (format: description | quantité | prix unitaire)</label>
        <textarea
          className="input min-h-[120px] py-2 font-mono text-xs"
          required
          placeholder="Ciment 50 sacs | 50 | 8000&#10;Fer à béton 12mm | 200 | 5000"
          value={form.lines_text}
          onChange={(e) => setForm({ ...form, lines_text: e.target.value })}
        />
        <p className="text-[10px] text-obsidian-500 mt-1">Une ligne par article. Séparateur : « | ». Total calculé automatiquement.</p>
      </div>
      <div>
        <label className="label block">Notes</label>
        <textarea className="input min-h-[60px] py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={close} className="btn-secondary">Annuler</button>
        <button type="submit" className="btn-primary">Créer le BC (brouillon)</button>
      </div>
    </form>
  );
}

function PurchaseDetailModal({ item, onClose, onDecide }) {
  if (!item) return null;
  const isPending = item.status === "draft" || item.status === "pending";
  return (
    <Modal open={!!item} onClose={onClose} title={`Bon de commande ${item.po_number}`} subtitle={`${item.supplier_name} · ${formatDate(item.date)}`} size="lg">
      <div className="space-y-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <th className="overline text-left px-3 py-2">Description</th>
              <th className="overline text-right px-3 py-2">Qté</th>
              <th className="overline text-right px-3 py-2">PU</th>
              <th className="overline text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {item.lines?.map((l, i) => (
              <tr key={i} className="border-b border-line/50">
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2 text-right font-mono">{l.qty}</td>
                <td className="px-3 py-2 text-right font-mono">{formatXAF(l.unit_price)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatXAF(l.qty * l.unit_price)}</td>
              </tr>
            ))}
            <tr className="font-bold">
              <td colSpan={3} className="px-3 py-3 text-right text-citron uppercase text-xs tracking-wider">Total TTC</td>
              <td className="px-3 py-3 text-right font-mono text-lg text-citron">{formatXAF(item.total)}</td>
            </tr>
          </tbody>
        </table>
        {item.notes && <div className="p-3 bg-obsidian-900 text-sm text-obsidian-300">{item.notes}</div>}
        {isPending && (
          <div className="flex justify-end gap-2 pt-2 border-t border-line">
            {item.status === "draft" && (
              <button onClick={() => onDecide(item.id, "pending")} className="btn-secondary">Soumettre pour validation</button>
            )}
            <button onClick={() => onDecide(item.id, "rejected")} className="btn-danger"><XCircle size={14} /> Rejeter</button>
            <button onClick={() => onDecide(item.id, "approved")} className="btn-primary"><CheckCircle2 size={14} /> Approuver</button>
          </div>
        )}
        {item.status === "approved" && (
          <div className="flex justify-end gap-2 pt-2 border-t border-line">
            <button onClick={() => onDecide(item.id, "received")} className="btn-secondary"><Truck size={14} /> Marquer reçu</button>
            <button onClick={() => onDecide(item.id, "paid")} className="btn-primary"><Banknote size={14} /> Marquer payé</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ==================== STOCK ====================
export function Stock() {
  return (
    <GenericModule
      title="Stock"
      subtitle="Articles en stock"
      dataTestId="stock"
      endpoint="/stock"
      newButtonLabel="Nouvel article"
      extractStats={(d) => [
        { label: "Articles", value: (d.items || []).length, count: true },
        { label: "Stock faible", value: d.low_stock_count || 0, count: true, accent: "warn" },
        { label: "Valeur totale", value: d.total_value || 0, money: true, accent: "citron", highlight: true },
      ]}
      columns={[
        { key: "code", label: "Code", type: "code" },
        { key: "name", label: "Article" },
        { key: "category", label: "Catégorie" },
        { key: "quantity", label: "Qté", type: "number", align: "right", digits: 2 },
        { key: "unit", label: "Unité" },
        { key: "buy_price", label: "PA", type: "money", align: "right" },
        { key: "sell_price", label: "PV", type: "money", align: "right" },
        {
          key: "alert", label: "Alerte", align: "center",
          render: (r) => r.quantity <= r.alert_level
            ? <span className="pill bg-bad/15 text-bad">Bas</span>
            : <span className="pill bg-ok/15 text-ok">OK</span>,
        },
      ]}
      fields={[
        { key: "code", label: "Code", required: true, group: 1 },
        { key: "name", label: "Nom", required: true, group: 1 },
        { key: "category", label: "Catégorie", placeholder: "Ex: Pièces, Consommables", group: 2 },
        { key: "unit", label: "Unité", placeholder: "pcs, kg, L", group: 2 },
        { key: "quantity", label: "Qté initiale", type: "number", step: "0.01", group: 3 },
        { key: "alert_level", label: "Seuil alerte", type: "number", group: 3 },
        { key: "buy_price", label: "Prix achat (XAF)", type: "number", group: 3 },
        { key: "sell_price", label: "Prix vente (XAF)", type: "number", group: 3 },
        { key: "location", label: "Emplacement" },
      ]}
      defaults={{ code: "", name: "", category: "Général", unit: "pcs", quantity: 0, alert_level: 5, buy_price: 0, sell_price: 0, location: "" }}
    />
  );
}

// ==================== FACTURATION ====================
export function Invoices() {
  const [viewing, setViewing] = useState(null);
  async function payInvoice(id) {
    await api.patch(`/invoices/${id}/status`, { status: "paid" });
    setViewing(null);
    window.location.reload();
  }
  return (
    <>
      <GenericModule
        title="Facturation"
        subtitle="Factures clients"
        dataTestId="invoices"
        endpoint="/invoices"
        newButtonLabel="Nouvelle facture"
        extractStats={(d) => [
          { label: "Total émis", value: d.total_emitted || 0, money: true },
          { label: "Impayées", value: d.total_unpaid || 0, money: true, accent: "warn" },
          { label: "Payées", value: d.total_paid || 0, money: true, accent: "ok", highlight: true },
        ]}
        columns={[
          { key: "invoice_number", label: "N°", type: "code" },
          { key: "date", label: "Date", type: "date" },
          { key: "client_name", label: "Client" },
          { key: "total_ht", label: "HT", type: "money", align: "right" },
          { key: "total_vat", label: "TVA", type: "money", align: "right" },
          { key: "total_ttc", label: "TTC", type: "money", align: "right" },
          {
            key: "status", label: "Statut", type: "pill",
            pillClass: (r) => ({
              emitted: "bg-warn/15 text-warn",
              partial: "bg-citron/15 text-citron",
              paid: "bg-ok/15 text-ok",
              cancelled: "bg-bad/15 text-bad",
            })[r.status],
          },
        ]}
        rowActions={(row) => (
          <button onClick={() => setViewing(row)} className="btn-ghost h-7 w-7 p-0 text-citron" title="Voir"><FileText size={12} /></button>
        )}
        fields={[]}
        defaults={{ client_name: "", date: new Date().toISOString().slice(0, 10), lines_text: "", notes: "" }}
        customForm={({ form, setForm, submit, close }) => (
          <InvoiceForm form={form} setForm={setForm} close={close} />
        )}
      />
      <InvoiceDetailModal item={viewing} onClose={() => setViewing(null)} onPay={payInvoice} />
    </>
  );
}

function InvoiceForm({ form, setForm, close }) {
  async function onSubmit(e) {
    e.preventDefault();
    const lines = (form.lines_text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const parts = l.split("|").map((p) => p.trim());
        return {
          description: parts[0] || "",
          qty: parseFloat(parts[1] || 0),
          unit_price: parseFloat(parts[2] || 0),
          vat_rate: parseFloat(parts[3] || 18),
        };
      }).filter((l) => l.description && l.qty > 0);
    if (lines.length === 0) return alert("Ajoutez au moins une ligne");
    try {
      await api.post("/invoices", {
        entity_id: form.entity_id,
        client_name: form.client_name,
        date: form.date,
        due_date: form.due_date || null,
        lines,
        notes: form.notes || "",
      });
      close();
      window.location.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  }
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label block">Client *</label>
          <input className="input" required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
        </div>
        <div>
          <label className="label block">Date *</label>
          <input type="date" className="input" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="label block">Échéance</label>
          <input type="date" className="input" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="label block">Lignes (description | qté | PU HT | TVA%)</label>
        <textarea
          className="input min-h-[120px] py-2 font-mono text-xs"
          required
          placeholder="Prestation conseil | 10 | 50000 | 18&#10;Fourniture matériel | 2 | 250000 | 18"
          value={form.lines_text}
          onChange={(e) => setForm({ ...form, lines_text: e.target.value })}
        />
        <p className="text-[10px] text-obsidian-500 mt-1">TVA Gabon : 18%. Total HT/TVA/TTC calculés automatiquement.</p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={close} className="btn-secondary">Annuler</button>
        <button type="submit" className="btn-primary">Émettre la facture</button>
      </div>
    </form>
  );
}

function InvoiceDetailModal({ item, onClose, onPay }) {
  if (!item) return null;
  return (
    <Modal open={!!item} onClose={onClose} title={`Facture ${item.invoice_number}`} subtitle={`${item.client_name} · ${formatDate(item.date)}`} size="lg">
      <div className="space-y-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <th className="overline text-left px-3 py-2">Description</th>
              <th className="overline text-right px-3 py-2">Qté</th>
              <th className="overline text-right px-3 py-2">PU HT</th>
              <th className="overline text-right px-3 py-2">TVA %</th>
              <th className="overline text-right px-3 py-2">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {item.lines?.map((l, i) => (
              <tr key={i} className="border-b border-line/50">
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2 text-right font-mono">{l.qty}</td>
                <td className="px-3 py-2 text-right font-mono">{formatXAF(l.unit_price)}</td>
                <td className="px-3 py-2 text-right font-mono">{l.vat_rate}%</td>
                <td className="px-3 py-2 text-right font-mono">{formatXAF(l.qty * l.unit_price)}</td>
              </tr>
            ))}
            <tr><td colSpan={4} className="px-3 py-1 text-right text-xs text-obsidian-400 uppercase">Total HT</td><td className="px-3 py-1 text-right font-mono">{formatXAF(item.total_ht)}</td></tr>
            <tr><td colSpan={4} className="px-3 py-1 text-right text-xs text-obsidian-400 uppercase">TVA</td><td className="px-3 py-1 text-right font-mono">{formatXAF(item.total_vat)}</td></tr>
            <tr className="font-bold"><td colSpan={4} className="px-3 py-3 text-right text-citron uppercase text-xs tracking-wider">Total TTC</td><td className="px-3 py-3 text-right font-mono text-lg text-citron">{formatXAF(item.total_ttc)}</td></tr>
          </tbody>
        </table>
        {item.status !== "paid" && (
          <div className="flex justify-end gap-2 pt-2 border-t border-line">
            <button onClick={() => onPay(item.id)} className="btn-primary"><Banknote size={14} /> Marquer comme payée</button>
          </div>
        )}
      </div>
    </Modal>
  );
}
